import { useState } from "react";
import axios from "axios";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Mono:wght@400;500&family=DM+Sans:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    background: #0a0a0f;
    color: #e8e6df;
    font-family: 'DM Sans', sans-serif;
    min-height: 100vh;
  }

  .app {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0;
  }

  .left-panel {
    background: #0d0d14;
    border-right: 1px solid #1e1e2e;
    padding: 48px 40px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    position: relative;
    overflow: hidden;
  }

  .left-panel::before {
    content: '';
    position: absolute;
    top: -80px; left: -80px;
    width: 300px; height: 300px;
    background: radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%);
    pointer-events: none;
  }

  .brand { display: flex; align-items: center; gap: 10px; }

  .brand-dot {
    width: 8px; height: 8px;
    background: #6366f1;
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.85); }
  }

  .brand-name {
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.12em;
    color: #6366f1;
    text-transform: uppercase;
  }

  .headline {
    font-family: 'DM Serif Display', serif;
    font-size: 38px;
    line-height: 1.15;
    color: #f0ede6;
    letter-spacing: -0.5px;
  }

  .headline em { font-style: italic; color: #818cf8; }

  .subline {
    font-size: 14px;
    color: #6b6b80;
    line-height: 1.7;
    max-width: 360px;
  }

  .form { display: flex; flex-direction: column; gap: 18px; flex: 1; }

  .field { display: flex; flex-direction: column; gap: 7px; }

  .field label {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #555568;
  }

  .field input, .field select {
    background: #13131f;
    border: 1px solid #1e1e2e;
    border-radius: 8px;
    padding: 11px 14px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #e8e6df;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    width: 100%;
  }

  .field input::placeholder { color: #3a3a50; }

  .field input:focus, .field select:focus {
    border-color: #4f46e5;
    box-shadow: 0 0 0 3px rgba(99,102,241,0.12);
  }

  .field select option { background: #13131f; }

  .row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

  .generate-btn {
    margin-top: 8px;
    background: #4f46e5;
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 14px 24px;
    font-size: 14px;
    font-weight: 500;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: background 0.2s, transform 0.15s;
  }

  .generate-btn:hover:not(:disabled) { background: #4338ca; transform: translateY(-1px); }

  .generate-btn:disabled {
    background: #2a2a3e;
    color: #555568;
    cursor: not-allowed;
    transform: none;
  }

  .spinner {
    width: 14px; height: 14px;
    border: 2px solid rgba(255,255,255,0.2);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .right-panel {
    background: #0a0a0f;
    padding: 48px 40px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }

  .panel-header { display: flex; align-items: center; justify-content: space-between; }

  .panel-title {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #555568;
  }

  .status-pill {
    font-family: 'DM Mono', monospace;
    font-size: 11px;
    padding: 4px 10px;
    border-radius: 20px;
    border: 1px solid;
    letter-spacing: 0.04em;
  }

  .status-idle { color: #3a3a50; border-color: #1e1e2e; }
  .status-loading { color: #f59e0b; border-color: rgba(245,158,11,0.3); animation: blink 1s ease-in-out infinite; }
  .status-done { color: #34d399; border-color: rgba(52,211,153,0.3); }
  .status-error { color: #f87171; border-color: rgba(248,113,113,0.3); }

  @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.4} }

  .empty-state {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 14px;
    text-align: center;
    opacity: 0.35;
  }

  .empty-icon {
    width: 48px; height: 48px;
    border: 1.5px dashed #3a3a50;
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px;
  }

  .empty-state p { font-size: 13px; color: #555568; line-height: 1.6; max-width: 220px; }

  .result-block {
    display: flex;
    flex-direction: column;
    gap: 20px;
    animation: fadeUp 0.4s ease forwards;
  }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .result-section { display: flex; flex-direction: column; gap: 8px; }

  .result-label {
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3d3d55;
  }

  .subject-box {
    background: #13131f;
    border: 1px solid #1e1e2e;
    border-radius: 8px;
    padding: 12px 14px;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    color: #a5b4fc;
    letter-spacing: 0.01em;
  }

  .body-box {
    background: #13131f;
    border: 1px solid #1e1e2e;
    border-radius: 8px;
    padding: 16px;
    font-size: 13.5px;
    color: #c8c5bc;
    line-height: 1.8;
    white-space: pre-wrap;
    font-family: 'DM Sans', sans-serif;
    min-height: 200px;
  }

  .action-row { display: flex; gap: 10px; flex-wrap: wrap; }

  .copy-btn {
    flex: 1;
    background: #13131f;
    border: 1px solid #1e1e2e;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    color: #8b8ba0;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }

  .copy-btn:hover { border-color: #4f46e5; color: #a5b4fc; }
  .copy-btn.copied { border-color: #34d399; color: #34d399; }

  .gmail-btn {
    flex: 1;
    background: #1a1a2e;
    border: 1px solid #2a2a4a;
    border-radius: 8px;
    padding: 10px 16px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    color: #818cf8;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 6px;
  }

  .gmail-btn:hover { background: #1e1e3a; border-color: #4f46e5; color: #a5b4fc; }

  .gmail-note {
    font-size: 11px;
    color: #3d3d55;
    text-align: center;
    line-height: 1.5;
  }

  .gmail-note span { color: #6366f1; }

  .reset-btn {
    background: transparent;
    border: 1px solid #1e1e2e;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    font-family: 'DM Sans', sans-serif;
    color: #555568;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s;
  }

  .reset-btn:hover { border-color: #3a3a50; color: #8b8ba0; }

  .error-box {
    background: rgba(248,113,113,0.06);
    border: 1px solid rgba(248,113,113,0.2);
    border-radius: 8px;
    padding: 16px;
    font-size: 13px;
    color: #fca5a5;
    line-height: 1.6;
  }

  @media (max-width: 768px) {
    .app { grid-template-columns: 1fr; }
    .right-panel { border-top: 1px solid #1e1e2e; }
    .row-2 { grid-template-columns: 1fr; }
    .headline { font-size: 28px; }
  }
`;

const ROLES = [
  "Frontend Developer",
  "Full Stack Developer",
  "Backend Developer",
  "React Developer",
  "Node.js Developer",
  "UI/UX Developer",
  "Software Engineer",
];

const INITIAL = {
  companyName: "",
  companyUrl: "",
  personName: "",
  personRole: "",
  myRole: "Full Stack Developer",
  myEmail: "",
  myPhone: "",
  myLinkedin: "",
};

export default function App() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const isValid =
    form.companyName && form.companyUrl && form.personName &&
    form.personRole && form.myEmail && form.myLinkedin;

  const generate = async () => {
    setStatus("loading");
    setResult(null);
    setError("");
    try {
      const res = await axios.post("https://outreach-gpt-backend.onrender.com/generate-email", form);
      setResult(res.data);
      setStatus("done");
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Something went wrong.";
      setError(msg);
      setStatus("error");
    }
  };

  const copyAll = () => {
    const text = `Subject: ${result.subject}\n\n${result.body}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openInGmail = () => {
    const subject = encodeURIComponent(result.subject);
    const body = encodeURIComponent(result.body);
    
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${subject}&body=${body}`;
    window.open(gmailUrl, "_blank");
  };

  const reset = () => {
    setResult(null);
    setStatus("idle");
    setError("");
  };

  const statusLabel = {
    idle: "Waiting",
    loading: "Generating...",
    done: "Email ready",
    error: "Error",
  }[status];

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        {/* LEFT */}
        <div className="left-panel">
          <div className="brand">
            <div className="brand-dot" />
            <span className="brand-name">OutreachGPT</span>
          </div>

          <div>
            <h1 className="headline">
              Cold emails that<br />
              <em>actually get replies.</em>
            </h1>
            <p className="subline" style={{ marginTop: 14 }}>
              Fill in the prospect details. AI researches the company, finds their pain point, and writes a personalized outreach email — in seconds.
            </p>
          </div>

          <div className="form">
            <div className="row-2">
              <div className="field">
                <label>Company name</label>
                <input placeholder="e.g. VectorShift" value={form.companyName} onChange={(e) => update("companyName", e.target.value)} />
              </div>
              <div className="field">
                <label>Company website</label>
                <input placeholder="https://vectorshift.ai" value={form.companyUrl} onChange={(e) => update("companyUrl", e.target.value)} />
              </div>
            </div>

            <div className="row-2">
              <div className="field">
                <label>Contact's name</label>
                <input placeholder="e.g. John Smith" value={form.personName} onChange={(e) => update("personName", e.target.value)} />
              </div>
              <div className="field">
                <label>Their role</label>
                <input placeholder="e.g. Founder, HR Manager" value={form.personRole} onChange={(e) => update("personRole", e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label>Role I'm applying for</label>
              <select value={form.myRole} onChange={(e) => update("myRole", e.target.value)}>
                {ROLES.map((r) => <option key={r}>{r}</option>)}
              </select>
            </div>

            <div className="row-2">
              <div className="field">
                <label>My email</label>
                <input placeholder="you@gmail.com" value={form.myEmail} onChange={(e) => update("myEmail", e.target.value)} />
              </div>
              <div className="field">
                <label>My phone</label>
                <input placeholder="+91 98765 43210" value={form.myPhone} onChange={(e) => update("myPhone", e.target.value)} />
              </div>
            </div>

            <div className="field">
              <label>My LinkedIn URL</label>
              <input placeholder="https://linkedin.com/in/yourname" value={form.myLinkedin} onChange={(e) => update("myLinkedin", e.target.value)} />
            </div>

            <button className="generate-btn" onClick={generate} disabled={!isValid || status === "loading"}>
              {status === "loading" ? (
                <><div className="spinner" /> Generating email...</>
              ) : (
                "Generate cold email"
              )}
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="right-panel">
          <div className="panel-header">
            <span className="panel-title">Generated email</span>
            <span className={`status-pill status-${status}`}>{statusLabel}</span>
          </div>

          {status === "idle" && (
            <div className="empty-state">
              <div className="empty-icon">✉</div>
              <p>Fill in the prospect details on the left and hit Generate.</p>
            </div>
          )}

          {status === "loading" && (
            <div className="empty-state">
              <div style={{ width: 32, height: 32, border: "2px solid #1e1e2e", borderTop: "2px solid #6366f1", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <p>Researching company and crafting your email...</p>
            </div>
          )}

          {status === "error" && (
            <>
              <div className="error-box"><strong>Error:</strong> {error}</div>
              <button className="reset-btn" onClick={reset}>Try again</button>
            </>
          )}

          {status === "done" && result && (
            <div className="result-block">
              <div className="result-section">
                <span className="result-label">Subject line</span>
                <div className="subject-box">{result.subject}</div>
              </div>

              <div className="result-section">
                <span className="result-label">Email body</span>
                <div className="body-box">{result.body}</div>
              </div>

              <div className="action-row">
                <button className={`copy-btn ${copied ? "copied" : ""}`} onClick={copyAll}>
                  {copied ? "✓ Copied!" : "Copy subject + body"}
                </button>
                <button className="gmail-btn" onClick={openInGmail}>
                  Open in Gmail
                </button>
                <button className="reset-btn" onClick={reset}>New email</button>
              </div>

              <p className="gmail-note">
                Gmail mein khulega → <span>Resume attach karo</span> → Send! 🚀
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}